import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "../components/AdminLayout";
import { TalentMapView } from "./TalentMap";
import { CategoryStars } from "../components/StarRating";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { refreshSidebarCounts } from "../lib/sidebarBus";
import { toast } from "sonner";
import {
  Users, UserCheck, Car, CalendarPlus, MapPin, Search, Download, Plus,
  Phone, Mail, Pencil, Trash2, Star, IdCard, Briefcase, X, Loader2, Map as MapIcon,
  ArrowLeft, Lock, Eye, Sparkles, UserPlus, RotateCcw, KeyRound, EyeOff, BadgeCheck,
} from "lucide-react";

const STATUS = {
  disponivel: { label: "Disponível", cls: "bg-emerald-100 text-emerald-700" },
  ocupado: { label: "Ocupado", cls: "bg-amber-100 text-amber-700" },
  inativo: { label: "Inativo", cls: "bg-rose-100 text-rose-700" },
};
const AVAIL = ["Integral", "Meio período", "Fins de semana", "Noites", "Flexível"];
const LANGS = ["Português", "Inglês", "Espanhol", "Crioulo", "Francês"];
const WEEKDAYS = [
  { id: 0, short: "Dom" }, { id: 1, short: "Seg" }, { id: 2, short: "Ter" },
  { id: 3, short: "Qua" }, { id: 4, short: "Qui" }, { id: 5, short: "Sex" },
  { id: 6, short: "Sáb" },
];
function formatWorkdays(days) {
  if (!days || days.length === 0) return "—";
  if (days.length === 7) return "Todos os dias";
  return [...days].sort((a, b) => a - b).map((d) => WEEKDAYS.find((w) => w.id === d)?.short).filter(Boolean).join(", ");
}

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export default function TalentBank() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = !!user?.is_super_admin;
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [positions, setPositions] = useState([]);
  const [view, setView] = useState("list"); // list | map
  const [filters, setFilters] = useState({
    q: "", city: "", status: "", has_car: "", has_license: "",
    availability: "", language: "", experience: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "") return;
        if (k === "has_car" || k === "has_license") params[k] = v === "yes";
        else params[k] = v;
      });
      const [r, s] = await Promise.all([
        api.get("/talent-bank", { params }),
        api.get("/talent-bank/stats"),
      ]);
      setRows(r.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Ao abrir a página, marca os cadastros públicos como vistos e zera o badge.
  useEffect(() => {
    api.post("/talent-bank/mark-seen")
      .then(() => refreshSidebarCounts())
      .catch(() => {});
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este cadastro?")) return;
    try {
      await api.delete(`/talent-bank/${id}`);
      toast.success("Cadastro excluído");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const onAssign = async (id) => {
    try {
      await api.post(`/talent-bank/${id}/assign`);
      toast.success("Serviço atribuído — colaborador marcado como ocupado");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  // --- Vínculo com equipe (pool compartilhado) ---
  const [teams, setTeams] = useState([]);
  const [linkTarget, setLinkTarget] = useState(null); // talento a vincular
  const [linkTeam, setLinkTeam] = useState("");
  const [linkTipo, setLinkTipo] = useState("temporario");
  const [linking, setLinking] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/positions").then((r) => setPositions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/rating-categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const openLink = (c) => { setLinkTarget(c); setLinkTeam(""); setLinkTipo("temporario"); };

  const confirmLink = async () => {
    if (!linkTarget) return;
    setLinking(true);
    try {
      await api.post(`/talent-bank/${linkTarget.id}/link`, {
        team_id: linkTeam || null,
        tipo: linkTipo,
      });
      toast.success("Colaborador adicionado à equipe");
      setLinkTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLinking(false);
    }
  };

  const onRelease = async (id) => {
    if (!window.confirm("Marcar como finalizado e liberar este colaborador para o pool?")) return;
    try {
      await api.post(`/talent-bank/${id}/release`);
      toast.success("Colaborador liberado — disponível novamente");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const onReturnToBank = async (id) => {
    if (!window.confirm("Devolver ao banco? O colaborador ficará inativo na sua empresa (histórico preservado) e livre para outras empresas.")) return;
    try {
      await api.post(`/talent-bank/${id}/return-to-bank`);
      toast.success("Colaborador devolvido ao banco — ativo e livre novamente");
      setDetailTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const onExport = async () => {
    try {
      const res = await api.get("/talent-bank/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "banco_colaboradores.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="talent-bank-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/dashboard" data-testid="tb-back"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 transition mb-1">
              <ArrowLeft size={15} /> Voltar ao painel
            </Link>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="text-blue-600" /> Banco de Colaboradores
            </h1>
            <p className="text-sm text-slate-500">Diretório de profissionais cadastrados</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border bg-white p-0.5" data-testid="tb-view-toggle">
              <button onClick={() => setView("list")} data-testid="tb-view-list"
                className={`inline-flex items-center gap-1.5 px-3 min-h-[36px] rounded-md text-sm font-medium transition ${view === "list" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                <Users size={15} /> Banco
              </button>
              <button onClick={() => setView("map")} data-testid="tb-view-map"
                className={`inline-flex items-center gap-1.5 px-3 min-h-[36px] rounded-md text-sm font-medium transition ${view === "map" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                <MapIcon size={15} /> Mapa
              </button>
            </div>
            {view === "list" && isAdmin && (
              <button onClick={onExport} data-testid="tb-export"
                className="inline-flex items-center gap-2 px-3 min-h-[40px] rounded-lg border bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium">
                <Download size={16} /> Exportar
              </button>
            )}
            {view === "list" && (
              <button onClick={() => setEditing({})} data-testid="tb-add"
                className="inline-flex items-center gap-2 px-3 min-h-[40px] rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
                <Plus size={16} /> Novo
              </button>
            )}
          </div>
        </div>

        {view === "map" ? (
          <TalentMapView />
        ) : (
        <>
        {/* Dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="tb-stats">
          <StatCard icon={Users} label="Total" value={stats?.total} color="text-blue-600" />
          <StatCard icon={UserCheck} label="Disponíveis" value={stats?.available} color="text-emerald-600" />
          <StatCard icon={Car} label="Com carro" value={stats?.with_car} color="text-indigo-600" />
          <StatCard icon={MapPin} label="Na sua região" value={stats?.nearby} color="text-amber-600" />
          <StatCard icon={CalendarPlus} label="Novos no mês" value={stats?.new_this_month} color="text-fuchsia-600" />
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border p-3 flex flex-wrap gap-2 items-center" data-testid="tb-filters">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="cm-input w-full pl-9" placeholder="Buscar por nome, cidade…" data-testid="tb-search"
              value={filters.q} onChange={(e) => setF("q", e.target.value)} />
          </div>
          <input className="cm-input !w-[130px] flex-none" placeholder="Cidade" data-testid="tb-f-city" value={filters.city} onChange={(e) => setF("city", e.target.value)} />
          <select className="cm-input !w-[130px] flex-none" data-testid="tb-f-status" value={filters.status} onChange={(e) => setF("status", e.target.value)}>
            <option value="">Status</option>
            <option value="disponivel">Disponível</option>
            <option value="ocupado">Ocupado</option>
            <option value="inativo">Inativo</option>
          </select>
          <select className="cm-input !w-[120px] flex-none" data-testid="tb-f-car" value={filters.has_car} onChange={(e) => setF("has_car", e.target.value)}>
            <option value="">Carro</option><option value="yes">Com carro</option><option value="no">Sem carro</option>
          </select>
          <select className="cm-input !w-[120px] flex-none" data-testid="tb-f-license" value={filters.has_license} onChange={(e) => setF("has_license", e.target.value)}>
            <option value="">Driver</option><option value="yes">Com Driver</option><option value="no">Sem Driver</option>
          </select>
          <select className="cm-input !w-[130px] flex-none" data-testid="tb-f-lang" value={filters.language} onChange={(e) => setF("language", e.target.value)}>
            <option value="">Idioma</option>{LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select className="cm-input !w-[150px] flex-none" data-testid="tb-f-avail" value={filters.availability} onChange={(e) => setF("availability", e.target.value)}>
            <option value="">Disponibilidade</option>{AVAIL.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="tb-empty">Nenhum colaborador encontrado.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tb-list">
            {rows.map((c) => (
              <TalentCard key={c.id} c={c} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin}
                onEdit={() => setEditing(c)} onDelete={() => onDelete(c.id)} onAssign={() => onAssign(c.id)}
                onLink={() => openLink(c)} onRelease={() => onRelease(c.id)}
                onDetail={() => setDetailTarget(c)} />
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {/* Modal de vínculo com equipe */}
      {linkTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="tb-link-modal"
          onClick={() => !linking && setLinkTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-bold">Adicionar à equipe</h3>
              <p className="text-sm text-slate-500">{linkTarget.full_name} · {linkTarget.city || "—"}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Equipe</label>
              <select className="cm-input w-full" data-testid="tb-link-team" value={linkTeam}
                onChange={(e) => setLinkTeam(e.target.value)}>
                <option value="">Sem equipe (só marcar ocupado)</option>
                {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de vínculo</label>
              <div className="grid grid-cols-2 gap-2">
                {[["temporario", "Ajudante (temporário)"], ["fixo", "Fixo"]].map(([v, lbl]) => (
                  <button key={v} type="button" data-testid={`tb-link-tipo-${v}`} onClick={() => setLinkTipo(v)}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border ${linkTipo === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Temporário: some do pool até você "Finalizar". Fixo: permanece na equipe.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setLinkTarget(null)} disabled={linking}
                className="flex-1 py-2 rounded-lg bg-slate-100 font-semibold text-sm">Cancelar</button>
              <button onClick={confirmLink} disabled={linking} data-testid="tb-link-confirm"
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white font-semibold text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50">
                {linking && <Loader2 size={14} className="animate-spin" />} Adicionar
              </button>
            </div>
          </div>
        </div>
      )}

      {editing !== null && (
        <TalentModal item={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {detailTarget && (
        <TalentDetailModal
          c={detailTarget}
          isAdmin={isAdmin}
          positions={positions}
          teams={teams}
          categories={categories}
          onClose={() => setDetailTarget(null)}
          onEdit={() => { const t = detailTarget; setDetailTarget(null); setEditing(t); }}
          onReturnToBank={() => onReturnToBank(detailTarget.id)}
          onHired={() => { setDetailTarget(null); load(); }}
        />
      )}
    </AdminLayout>
  );
}

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border p-4">
    <Icon className={color} size={20} />
    <div className="text-2xl font-bold mt-2">{value ?? "—"}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);

function TalentCard({ c, isAdmin, isSuperAdmin, onEdit, onDelete, onAssign, onLink, onRelease, onDetail }) {
  const st = STATUS[c.status] || STATUS.disponivel;
  const isPublic = c.source === "public";
  const canDelete = isAdmin && (!isPublic || isSuperAdmin);
  const isOcupado = c.status === "ocupado";
  const isEfetivado = c.assignment_type === "efetivado";
  return (
    <div className="bg-white rounded-xl border p-4 flex flex-col" data-testid={`tb-card-${c.id}`}>
      <div className="flex items-start gap-3">
        {c.photo_path ? (
          <img src={fileUrl(c.photo_path)} alt={c.full_name} className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-lg">
            {(c.full_name || "?").charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{c.full_name}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} /> {c.city || "—"}</div>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${st.cls}`}>{st.label}</span>
          {isOcupado && c.assigned_tenant_name && (
            <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid={`tb-assigned-${c.id}`}>
              <Briefcase size={11} /> {c.assigned_tenant_name}
              {c.assignment_type === "fixo" && <span className="px-1 rounded bg-amber-100">fixo</span>}
            </div>
          )}
        </div>
        {c.rating != null && (
          <div className="flex items-center gap-0.5 text-amber-500 text-sm font-semibold"><Star size={14} fill="currentColor" /> {c.rating}</div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
        {c.has_car && <Chip icon={Car}>Carro</Chip>}
        {c.has_license && <Chip icon={IdCard}>Driver</Chip>}
        {c.availability && <Chip icon={Briefcase}>{c.availability}</Chip>}
        {(c.languages || []).map((l) => <Chip key={l}>{l}</Chip>)}
      </div>
      {c.experience && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{c.experience}</p>}

      {/* Ação principal: adicionar à equipe (pool) / finalizar */}
      {isAdmin && (
        isEfetivado ? (
          <button onClick={onDetail} data-testid={`tb-manage-${c.id}`}
            className="mt-3 w-full min-h-[38px] rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-semibold inline-flex items-center justify-center gap-1.5">
            <BadgeCheck size={15} /> Efetivado · gerenciar
          </button>
        ) : isOcupado ? (
          <button onClick={onRelease} data-testid={`tb-release-${c.id}`}
            className="mt-3 w-full min-h-[38px] rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm font-semibold inline-flex items-center justify-center gap-1.5">
            <UserCheck size={15} /> Marcar como finalizado (liberar)
          </button>
        ) : (
          <button onClick={onLink} data-testid={`tb-link-${c.id}`}
            className="mt-3 w-full min-h-[38px] rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold inline-flex items-center justify-center gap-1.5">
            <CalendarPlus size={15} /> Adicionar à equipe
          </button>
        )
      )}

      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t">
        {c.phone && <IconBtn href={`tel:${c.phone}`} title="Ligar" testid={`tb-call-${c.id}`}><Phone size={16} /></IconBtn>}
        {c.email && <IconBtn href={`mailto:${c.email}`} title="E-mail" testid={`tb-mail-${c.id}`}><Mail size={16} /></IconBtn>}
        <button onClick={onDetail} data-testid={`tb-detail-${c.id}`} title="Ver detalhes"
          className="ml-auto min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"><Eye size={16} /></button>
        <button onClick={onEdit} data-testid={`tb-edit-${c.id}`} title="Editar"
          className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"><Pencil size={16} /></button>
        {canDelete ? (
          <button onClick={onDelete} data-testid={`tb-delete-${c.id}`} title="Excluir"
            className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 size={16} /></button>
        ) : isAdmin && isPublic ? (
          <span data-testid={`tb-delete-locked-${c.id}`} title="Somente o super admin pode excluir cadastros feitos pelo site"
            className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg text-slate-300 cursor-not-allowed"><Lock size={15} /></span>
        ) : null}
      </div>
    </div>
  );
}

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
    {Icon && <Icon size={11} />}{children}
  </span>
);
const IconBtn = ({ href, title, children, testid }) => (
  <a href={href} title={title} data-testid={testid}
    className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100">{children}</a>
);

function TalentModal({ item, categories, onClose, onSaved }) {
  const isNew = !item.id;
  const [f, setF] = useState({
    full_name: "", phone: "", email: "", address: "", city: "", state: "",
    zip_code: "", has_license: false, has_car: false, vehicle_type: "",
    languages: [], work_days: [], availability: "", experience: "", status: "disponivel",
    ratings: {}, ...item,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setRating = (catId, star) => setF((p) => ({ ...p, ratings: { ...(p.ratings || {}), [catId]: star } }));
  const toggleLang = (l) => setF((p) => ({
    ...p, languages: (p.languages || []).includes(l) ? p.languages.filter((x) => x !== l) : [...(p.languages || []), l],
  }));

  const save = async () => {
    if (!f.full_name?.trim()) return toast.error("Informe o nome");
    setSaving(true);
    try {
      const payload = { ...f };
      // limpa zeros do dict de notas
      payload.ratings = Object.fromEntries(
        Object.entries(payload.ratings || {}).filter(([, v]) => v > 0)
      );
      delete payload.rating; // backend recalcula a média
      if (payload.travel_radius_miles === "" || payload.travel_radius_miles == null) delete payload.travel_radius_miles;
      else payload.travel_radius_miles = Number(payload.travel_radius_miles);
      if (isNew) await api.post("/talent-bank", payload);
      else await api.patch(`/talent-bank/${item.id}`, payload);
      toast.success(isNew ? "Cadastro criado" : "Cadastro atualizado");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="tb-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{isNew ? "Novo colaborador" : "Editar colaborador"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <input className="cm-input w-full" placeholder="Nome completo *" data-testid="tb-m-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <input className="cm-input w-full" placeholder="Telefone" value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} />
            <input className="cm-input w-full" placeholder="E-mail" value={f.email || ""} onChange={(e) => set("email", e.target.value)} />
          </div>
          <input className="cm-input w-full" placeholder="Endereço" value={f.address || ""} onChange={(e) => set("address", e.target.value)} />
          <div className="grid grid-cols-3 gap-3">
            <input className="cm-input w-full" placeholder="Cidade" value={f.city || ""} onChange={(e) => set("city", e.target.value)} />
            <input className="cm-input w-full" placeholder="Estado" value={f.state || ""} onChange={(e) => set("state", e.target.value)} />
            <input className="cm-input w-full" placeholder="CEP" value={f.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-4 h-4" checked={!!f.has_license} onChange={(e) => set("has_license", e.target.checked)} /> Possui Driver</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-4 h-4" checked={!!f.has_car} onChange={(e) => set("has_car", e.target.checked)} /> Possui carro</label>
          </div>
          <input className="cm-input w-full" placeholder="Tipo de veículo" value={f.vehicle_type || ""} onChange={(e) => set("vehicle_type", e.target.value)} />
          <div className="flex flex-wrap gap-1.5">
            {LANGS.map((l) => (
              <button type="button" key={l} onClick={() => toggleLang(l)}
                className={`px-2.5 py-1 rounded-full text-xs border ${(f.languages || []).includes(l) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600"}`}>{l}</button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Dias de trabalho na semana</label>
            <div className="flex flex-wrap gap-1.5" data-testid="tb-m-workdays">
              {WEEKDAYS.map((w) => {
                const active = (f.work_days || []).includes(w.id);
                return (
                  <button type="button" key={w.id} data-testid={`tb-m-wd-${w.id}`}
                    onClick={() => setF((p) => ({
                      ...p,
                      work_days: (p.work_days || []).includes(w.id)
                        ? p.work_days.filter((x) => x !== w.id)
                        : [...(p.work_days || []), w.id].sort((a, b) => a - b),
                    }))}
                    className={`w-12 h-10 rounded-lg text-xs font-semibold border ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>
                    {w.short}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className="cm-input w-full" value={f.availability || ""} onChange={(e) => set("availability", e.target.value)}>
              <option value="">Disponibilidade</option>{AVAIL.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select className="cm-input w-full" value={f.status} onChange={(e) => set("status", e.target.value)}>
              <option value="disponivel">Disponível</option><option value="ocupado">Ocupado</option><option value="inativo">Inativo</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Raio de alcance (milhas) — até onde topa ir</label>
            <input className="cm-input w-full" type="number" min="0" step="5" data-testid="tb-m-radius"
              placeholder="Ex.: 30"
              value={f.travel_radius_miles ?? ""} onChange={(e) => set("travel_radius_miles", e.target.value === "" ? "" : Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Avaliação por modalidade</label>
            <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/60">
              <CategoryStars categories={categories} values={f.ratings || {}} onChange={setRating} testid="tb-m-ratings" />
            </div>
          </div>
          <textarea className="cm-input w-full min-h-[70px]" placeholder="Experiência" value={f.experience || ""} onChange={(e) => set("experience", e.target.value)} />
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border text-slate-600">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="tb-m-save"
            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="animate-spin" size={16} />} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}


function TalentDetailModal({ c, isAdmin, positions, teams, categories, onClose, onEdit, onReturnToBank, onHired }) {
  const [mode, setMode] = useState("view"); // view | hire
  const st = STATUS[c.status] || STATUS.disponivel;
  const isEfetivado = c.assignment_type === "efetivado";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="tb-detail-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Detalhes do colaborador</h2>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={onEdit} data-testid="tb-detail-edit" title="Editar dados"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-slate-600 hover:bg-slate-50 text-sm font-semibold">
                <Pencil size={15} /> Editar dados
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
          </div>
        </div>

        <div className="flex items-start gap-3">
          {c.photo_path ? (
            <img src={fileUrl(c.photo_path)} alt={c.full_name} className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-xl">{(c.full_name || "?").charAt(0)}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold">{c.full_name}</div>
            <div className="text-sm text-slate-500 flex items-center gap-1"><MapPin size={13} /> {[c.city, c.state].filter(Boolean).join(", ") || "—"}</div>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${st.cls}`}>{st.label}</span>
          </div>
        </div>

        {isEfetivado && (
          <div className="mt-3 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-800 text-sm px-3 py-2 flex items-center gap-2" data-testid="tb-detail-efetivado">
            <BadgeCheck size={16} /> Efetivado como colaborador em <strong>{c.assigned_tenant_name || "sua empresa"}</strong>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-sm">
          <Info label="Telefone" value={c.phone} />
          <Info label="E-mail" value={c.email} />
          <Info label="Endereço" value={c.address} />
          <Info label="CEP" value={c.zip_code} />
          <Info label="Carro" value={c.has_car ? "Sim" : "Não"} />
          <Info label="Driver" value={c.has_license ? "Sim" : "Não"} />
          <Info label="Idiomas" value={(c.languages || []).join(", ")} />
          <Info label="Disponibilidade" value={c.availability} />
          <Info label="Dias de trabalho" value={formatWorkdays(c.work_days)} />
          <Info label="Raio de alcance" value={c.travel_radius_miles != null ? `${c.travel_radius_miles} mi` : null} />
          <Info label="Avaliação média" value={c.rating != null ? `⭐ ${c.rating}` : null} />
        </div>
        {categories && categories.length > 0 && c.ratings && Object.keys(c.ratings).length > 0 && (
          <div className="mt-3" data-testid="tb-detail-ratings">
            <div className="text-xs font-semibold text-slate-500 mb-1.5">Avaliação por modalidade</div>
            <div className="rounded-xl border border-slate-200 p-3">
              <CategoryStars categories={categories} values={c.ratings || {}} readOnly testid="tb-detail-cat-stars" />
            </div>
          </div>
        )}
        {c.experience && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-slate-500 mb-0.5">Experiência</div>
            <p className="text-sm text-slate-700">{c.experience}</p>
          </div>
        )}

        {isAdmin && mode === "view" && (
          <div className="flex gap-2 mt-5 pt-4 border-t">
            {isEfetivado ? (
              <button onClick={onReturnToBank} data-testid="tb-return-to-bank"
                className="flex-1 py-2.5 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 font-semibold text-sm inline-flex items-center justify-center gap-2">
                <RotateCcw size={16} /> Devolver ao banco
              </button>
            ) : (
              <button onClick={() => setMode("hire")} data-testid="tb-hire-open"
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 font-semibold text-sm inline-flex items-center justify-center gap-2">
                <UserPlus size={16} /> Contratar / Efetivar
              </button>
            )}
          </div>
        )}

        {isAdmin && mode === "hire" && (
          <HireForm c={c} positions={positions} teams={teams} onCancel={() => setMode("view")} onHired={onHired} />
        )}
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs font-semibold text-slate-400">{label}</div>
    <div className="text-slate-700 truncate">{value || "—"}</div>
  </div>
);

function HireForm({ c, positions, teams, onCancel, onHired }) {
  const [f, setF] = useState({
    email: c.email || "",
    password: genPassword(),
    role: "collaborator",
    position: "",
    team_id: "",
    valor_por_dia: 0,
    zelle_venmo: "",
    ssn: "",
    status: "ativo",
    workdays: Array.isArray(c.work_days) ? c.work_days : [],
  });
  const [showPw, setShowPw] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.email.trim()) return toast.error("Informe o e-mail de acesso");
    if (!f.password || f.password.length < 6) return toast.error("Senha muito curta (mín. 6)");
    setSaving(true);
    try {
      await api.post(`/talent-bank/${c.id}/hire`, {
        email: f.email.trim(),
        password: f.password,
        role: f.role,
        position: f.position || null,
        team_id: f.team_id || null,
        valor_por_dia: parseFloat(f.valor_por_dia) || 0,
        zelle_venmo: f.zelle_venmo || null,
        ssn: f.ssn || null,
        status: f.status,
        workdays: f.workdays,
      });
      toast.success(`${c.full_name} efetivado como colaborador`);
      onHired();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 pt-4 border-t space-y-3" data-testid="tb-hire-form">
      <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
        <Sparkles size={15} className="text-emerald-600" /> Efetivar como colaborador
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">E-mail de acesso *</label>
          <input className="cm-input w-full" type="email" data-testid="tb-hire-email" value={f.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Senha *</label>
          <div className="flex gap-1.5">
            <div className="relative flex-1">
              <input className="cm-input w-full pr-9" type={showPw ? "text" : "password"} data-testid="tb-hire-password" value={f.password} onChange={(e) => set("password", e.target.value)} />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <button type="button" onClick={() => set("password", genPassword())} title="Gerar senha" data-testid="tb-hire-genpw"
              className="px-2.5 rounded-lg border bg-white text-slate-600 hover:bg-slate-50"><KeyRound size={15} /></button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Perfil</label>
          <select className="cm-input w-full" data-testid="tb-hire-role" value={f.role} onChange={(e) => set("role", e.target.value)}>
            <option value="collaborator">Colaborador</option>
            <option value="supervisor">Supervisor</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Cargo</label>
          <select className="cm-input w-full" data-testid="tb-hire-position" value={f.position} onChange={(e) => set("position", e.target.value)}>
            <option value="">— selecione —</option>
            {(positions || []).map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Equipe</label>
          <select className="cm-input w-full" data-testid="tb-hire-team" value={f.team_id} onChange={(e) => set("team_id", e.target.value)}>
            <option value="">— sem equipe —</option>
            {(teams || []).map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor por dia (US$)</label>
          <input className="cm-input w-full" type="number" step="0.01" min="0" data-testid="tb-hire-valor" value={f.valor_por_dia} onChange={(e) => set("valor_por_dia", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Zelle / Venmo</label>
          <input className="cm-input w-full" data-testid="tb-hire-zelle" value={f.zelle_venmo} onChange={(e) => set("zelle_venmo", e.target.value)} placeholder="@usuario ou telefone" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">SSN / ITIN</label>
          <input className="cm-input w-full" data-testid="tb-hire-ssn" value={f.ssn} onChange={(e) => set("ssn", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Status</label>
          <select className="cm-input w-full" data-testid="tb-hire-status" value={f.status} onChange={(e) => set("status", e.target.value)}>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Dias de trabalho na semana</label>
        <div className="flex flex-wrap gap-1.5" data-testid="tb-hire-workdays">
          {WEEKDAYS.map((w) => {
            const active = (f.workdays || []).includes(w.id);
            return (
              <button type="button" key={w.id} data-testid={`tb-hire-wd-${w.id}`}
                onClick={() => set("workdays", active ? f.workdays.filter((x) => x !== w.id) : [...f.workdays, w.id].sort((a, b) => a - b))}
                className={`w-12 h-10 rounded-lg text-xs font-semibold border ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>
                {w.short}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onCancel} disabled={saving} className="flex-1 py-2.5 rounded-xl border text-slate-600 font-semibold text-sm">Voltar</button>
        <button onClick={submit} disabled={saving} data-testid="tb-hire-submit"
          className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="animate-spin" size={16} />} Efetivar colaborador
        </button>
      </div>
    </div>
  );
}
